class ClickAirPdpGallery extends HTMLElement {
  connectedCallback() {
    this.viewport = this.querySelector('[data-pg-viewport]');
    this.slides = Array.from(this.querySelectorAll('[data-pg-slide]'));
    this.prevBtn = this.querySelector('[data-pg-prev]');
    this.nextBtn = this.querySelector('[data-pg-next]');
    this.dotsWrap = this.querySelector('[data-pg-dots]');

    if (!this.viewport || this.slides.length === 0) return;

    this.index = 0;

    this.onPrev = () => this.step(-1);
    this.onNext = () => this.step(1);
    this.onResize = () => {
      this.syncSlideWidths();
      this.goTo(this.index, false);
    };
    this.onScroll = () => this.syncDotsFromScroll();

    this.prevBtn?.addEventListener('click', this.onPrev);
    this.nextBtn?.addEventListener('click', this.onNext);
    window.addEventListener('resize', this.onResize);
    this.viewport.addEventListener('scroll', this.onScroll, { passive: true });

    this.buildDots();
    this.syncSlideWidths();
    this.goTo(0, false);

    if (this.slides.length <= 1) {
      this.classList.add('is-single');
    }
  }

  disconnectedCallback() {
    this.prevBtn?.removeEventListener('click', this.onPrev);
    this.nextBtn?.removeEventListener('click', this.onNext);
    window.removeEventListener('resize', this.onResize);
    this.viewport?.removeEventListener('scroll', this.onScroll);
  }

  get maxIndex() {
    return Math.max(0, this.slides.length - 1);
  }

  getSlideWidth() {
    return this.viewport?.clientWidth || 0;
  }

  syncSlideWidths() {
    const slideWidth = this.getSlideWidth();
    if (!slideWidth) return;

    this.style.setProperty('--ca-pf-slide-width', `${slideWidth}px`);
    this.slides.forEach((slide) => {
      slide.style.flexBasis = `${slideWidth}px`;
      slide.style.width = `${slideWidth}px`;
      slide.style.minWidth = `${slideWidth}px`;
    });
  }

  buildDots() {
    if (!this.dotsWrap) return;

    this.dotsWrap.innerHTML = '';
    this.slides.forEach((_, i) => {
      const dot = document.createElement('button');
      dot.type = 'button';
      dot.className = 'ca-pf__gallery-dot';
      dot.setAttribute('aria-label', `Go to image ${i + 1}`);
      dot.addEventListener('click', (event) => {
        event.preventDefault();
        this.goTo(i);
      });
      this.dotsWrap.appendChild(dot);
    });

    this.dots = Array.from(this.dotsWrap.querySelectorAll('.ca-pf__gallery-dot'));
  }

  step(delta) {
    this.goTo(this.index + delta);
  }

  goTo(targetIndex, smooth = true) {
    const nextIndex = Math.min(Math.max(targetIndex, 0), this.maxIndex);
    const slideWidth = this.getSlideWidth();

    if (slideWidth > 0) {
      this.viewport.scrollTo({
        left: nextIndex * slideWidth,
        behavior: smooth ? 'smooth' : 'auto',
      });
    }

    this.index = nextIndex;
    this.updateDots();
    this.updateButtons();
  }

  syncDotsFromScroll() {
    const slideWidth = this.getSlideWidth();
    if (!slideWidth) return;

    const nextIndex = Math.min(
      Math.max(Math.round(this.viewport.scrollLeft / slideWidth), 0),
      this.maxIndex
    );

    if (nextIndex === this.index) return;

    this.index = nextIndex;
    this.updateDots();
    this.updateButtons();
  }

  updateButtons() {
    if (!this.prevBtn || !this.nextBtn) return;

    this.prevBtn.disabled = this.index <= 0;
    this.nextBtn.disabled = this.index >= this.maxIndex;
  }

  updateDots() {
    if (!this.dots) return;

    this.dots.forEach((dot, i) => {
      const isActive = i === this.index;
      dot.classList.toggle('is-active', isActive);
      dot.setAttribute('aria-current', isActive ? 'true' : 'false');
    });
  }
}

if (!customElements.get('clickair-pdp-gallery')) {
  customElements.define('clickair-pdp-gallery', ClickAirPdpGallery);
}
